import { Component, OnDestroy, OnInit } from '@angular/core';
import { Title } from '@angular/platform-browser';
import Chart from 'chart.js/auto';
import { LayoutService } from '../../../core/services/layout/layout';
import {
  AssistanceCost,
  FundingSource,
  KpiIndicator,
  KpiIndicatorsResponse,
  SectorCost,
} from '../../../core/models/kpi.interface';
import { Subscription } from 'rxjs';
import { DashboardService } from '../../../core/services/dashboardService/dashboard-service';
import { TermPipe } from '../../../shared/pipes/term.pipe';
import { TermKey } from '../../../core/models/terminology.interface';
import { ChangelogService } from '../../../core/services/changelog/changelog-service';
import { ChangelogModalComponent } from '../changelogs/changelogs.component';
import { MatDialog } from '@angular/material/dialog';

@Component({
  selector: 'app-home',
  imports: [TermPipe],
  templateUrl: './home.html',
  styleUrl: './home.css',
})
export class Home implements OnInit, OnDestroy {
  kpiTable: KpiIndicator[] = [];
  private sectorChart?: Chart;
  private fundingChart?: Chart;
  private assistanceChart?: Chart;
  private sectorSub?: Subscription;
  termkey = TermKey;

  constructor(
    private title: Title,
    private layoutService: LayoutService,
    private dashboardService: DashboardService,
    private changelogService: ChangelogService,
    private dialog: MatDialog,
  ) {
    this.title.setTitle('inicio');
  }

  ngOnInit() {
    this.checkShowChangelog();
    this.sectorSub = this.layoutService.sectorSelected$.subscribe((sector) => {
      const sectorName = sector?.name ?? '';
      this.loadDashboard(sectorName);
    });
  }

      checkShowChangelog(): void {
    this.changelogService.getlatest().subscribe({
      next: (data) => {
        if (data?.show_modal) {
          this.dialog.open(ChangelogModalComponent, {
            width: '650px',
            disableClose: true,
            data,
          });
        }
      },
      error: (err) => console.error('Error al verificar changelog:', err),
    });
  }

  ngOnDestroy() {
    this.sectorSub?.unsubscribe();
  }

  private loadDashboard(sectorName: string) {
    this.dashboardService
      .getCostsBySector(sectorName)
      .subscribe((data) => this.renderCostsBySectorChart(data));
    this.dashboardService
      .getFundingSources(sectorName)
      .subscribe((data) => this.renderFundingSourceChart(data));
    this.dashboardService
      .getCostsByAssistance(sectorName)
      .subscribe((data) => this.renderCostsByAssistanceChart(data));
    this.dashboardService
      .getKpiIndicators(sectorName)
      .subscribe((data) => (this.kpiTable = this.mapKpiIndicators(data)));
  }
  private mapKpiIndicators(data: KpiIndicatorsResponse): KpiIndicator[] {
    return [
      { item: 'Número de cargos registrados', valor: data.employees },
      { item: 'Total de actividades por territorio', valor: data.activities },
      { item: 'Total de costos por infraestructura (global)', valor: data.infraestructureTotal },
      { item: 'Total de costos por bienes en inmuebles (global)', valor: data.fixedAssetTotal },
      { item: 'Total de costos por insumos (global)', valor: data.consumableTotal },
    ];
  }

  private buildSectorDatasets(raw: SectorCost[]) {
    const terms = [...new Set(raw.map((r) => r.term))].sort((a, b) => a - b);
    const sectors = [...new Set(raw.map((r) => r.sector))];
    const colors = ['#1e7e34', '#0d6efd', '#dc3545', '#fd7e14', '#6f42c1'];

    const datasets = sectors.map((sector, i) => ({
      label: sector.charAt(0).toUpperCase() + sector.slice(1),
      data: terms.map(
        (term) => raw.find((r) => r.sector === sector && r.term === term)?.total ?? 0,
      ),
      borderColor: colors[i % colors.length],
      backgroundColor: colors[i % colors.length] + '33',
      fill: true,
      tension: 0.4,
    }));

    return { labels: terms.map(String), datasets };
  }

  private renderCostsBySectorChart(raw: SectorCost[]) {
    const { labels, datasets } = this.buildSectorDatasets(raw);

    this.sectorChart?.destroy();
    const ctx = document.getElementById('costsBySectorChart') as HTMLCanvasElement;
    this.sectorChart = new Chart(ctx, {
      type: 'line',
      data: { labels, datasets },
      options: {
        responsive: true,
        maintainAspectRatio: false,
        plugins: {
          legend: { display: datasets.length > 1 },
        },
        scales: {
          y: {
            ticks: { callback: (value) => '$' + Number(value).toLocaleString('es-CO') },
          },
        },
      },
    });
  }

  private renderFundingSourceChart(raw: FundingSource[]) {
    this.fundingChart?.destroy();
    const ctx = document.getElementById('fundingSourceChart') as HTMLCanvasElement;
    this.fundingChart = new Chart(ctx, {
      type: 'line',
      data: {
        labels: raw.map((r) => r.month),
        datasets: [
          {
            label: 'Fuentes de financiación',
            data: raw.map((r) => r.total),
            borderColor: '#0d6efd',
            backgroundColor: 'rgba(13,110,253,0.2)',
            fill: true,
            tension: 0.4,
          },
        ],
      },
      options: {
        responsive: true,
        maintainAspectRatio: false,
        plugins: { legend: { display: false } },
      },
    });
  }

  private renderCostsByAssistanceChart(raw: AssistanceCost[]) {
    this.assistanceChart?.destroy();
    const ctx = document.getElementById('costsByAssistanceChart') as HTMLCanvasElement;
    this.assistanceChart = new Chart(ctx, {
      type: 'bar',
      data: {
        labels: raw.map((r) => r.assistence),
        datasets: [
          {
            label: 'Costo',
            data: raw.map((r) => r.total),
            backgroundColor: ['#1e7e34', '#0d6efd', '#dc3545'],
          },
        ],
      },
      options: {
        maintainAspectRatio: false,
        responsive: true,
        plugins: { legend: { display: false } },
      },
    });
  }
}
